import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import type { Branch, Webhook, WebhookTestResult } from '../lib/types'
import {
  branchSummary,
  draftOf,
  emptyDraft,
  EVENT_LABEL,
  isPlainHttp,
  webhookProblems,
  WEBHOOK_EVENTS,
  type WebhookDraft,
  type WebhookEvent,
} from '../lib/webhooks'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Mark, Masked, OneTimeSecret, TestResult } from './connectParts'
import { Confirm, Toggle, useFlash, useSave } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, Tick, useOpenRecord } from './settingsKit'
import '../styles/connect.css'

/**
 * Webhooks: the organisation's own systems told what happens at its branches.
 *
 * The installation owner's (tenant.manage), as the directory connection is. One
 * row per endpoint, opening a drawer to change it, test it with the real signer
 * and guard, give it a new secret or delete it. The secret is shown once, in the
 * dark block above the list, until somebody says they have copied it — it is the
 * only time the server will ever send it. Secrets are held per endpoint, so
 * making a second one for another endpoint never replaces one not yet copied.
 */
export function Webhooks({ organisation, branches }: { organisation?: string; branches: Branch[] }) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [rows, setRows] = useState<Webhook[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')
  // Endpoint id → the secret the server just sent for it, until it is copied.
  const [secrets, setSecrets] = useState<Record<string, string>>({})
  const showSecret = (id: string, value: string) => setSecrets((s) => ({ ...s, [id]: value }))
  const forgetSecret = (id: string) =>
    setSecrets((s) => {
      const { [id]: _, ...rest } = s
      return rest
    })

  const branchesHere = organisation ? branches.filter((b) => b.tenantId === organisation) : branches

  const load = useCallback(async () => {
    try {
      const { webhooks } = await api.webhooks(organisation)
      setRows(webhooks)
      setLoaded(true)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    }
    // lang deliberately not a dependency: a language switch must not reload.
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  const on = rows.filter((w) => w.active).length

  // The webhook open on its own page (#webhooks/<id>, #webhooks/new).
  const rec = useOpenRecord(rows, (w) => w.id, loaded)
  if ((rec.isNew && loaded) || rec.row) {
    return (
      <>
        {toast}
        <HookDrawer
          key={rec.code}
          hook={rec.row ?? null}
          organisation={organisation}
          branches={branchesHere}
          flash={flash}
          onClose={rec.close}
          onCreated={(webhook, secret) => {
            // Listed at once: were the reload to fail, the only copy of the secret would be unseen.
            setRows((r) => (r.some((x) => x.id === webhook.id) ? r : [...r, webhook]))
            showSecret(webhook.id, secret)
            rec.close()
            void load()
          }}
          onSecret={(id, secret) => {
            showSecret(id, secret)
            rec.close()
          }}
          onChanged={async () => {
            await load()
            rec.close()
          }}
          onDeleted={async (id) => {
            forgetSecret(id)
            await load()
            rec.close()
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.webhooks')}
        line={say('connect.line.webhooks')}
        actions={
          <Button variant="primary" isDisabled={!loaded} onPress={() => rec.open()}>
            <Icon name="plus" size={16} /> {say('webhooks.add')}
          </Button>
        }
      />
      {error && <Notice className="connect-gap">{error}</Notice>}

      {rows
        .filter((w) => w.id in secrets)
        .map((w) => (
          <div key={w.id} className="connect-gap">
            <OneTimeSecret
              title={say('connect.secretFor', { name: w.name })}
              note={say('connect.secretOnce')}
              value={secrets[w.id] ?? ''}
              labels={{ show: say('connect.show'), hide: say('connect.hide'), copy: say('connect.copySecret'), done: say('webhooks.secretDone') }}
              onCopied={() => flash(say('connect.secretCopied'))}
              onDone={() => forgetSecret(w.id)}
            />
          </div>
        ))}

      <ListCard title={say('connect.webhooks.list')} count={loaded ? say('connect.endpointCount', { n: rows.length, on }) : undefined}>
        {!loaded && !error && <p className="empty">{say('common.loading')}</p>}
        {loaded && rows.length === 0 && <EmptyState icon={<Icon name="link" size={20} />} title={say('webhooks.none')} />}
        {rows.length > 0 && (
          <ListTable
            label={say('connect.webhooks.list')}
            cols="minmax(0, 2.2fr) 120px minmax(0, 1fr) 96px 16px"
            head={[say('connect.col.endpoint'), say('connect.col.events'), say('connect.col.branches'), say('connect.col.status'), '']}
          >
            {rows.map((w) => {
              const events = w.events.filter((e): e is WebhookEvent => e in EVENT_LABEL)
              return (
                <ListRow key={w.id} off={!w.active} label={w.name} onOpen={() => rec.open(w)}>
                  <NameCell
                    tile={<IconTile icon="link" tone={w.active ? 1 : 6} size="sm" />}
                    name={
                      <>
                        {w.name}
                        {isPlainHttp(w.url) && (
                          <Chip size="sm" variant="soft" color="warning">
                            {say('connect.unencrypted')}
                          </Chip>
                        )}
                      </>
                    }
                    sub={<span className="connect-mono">{w.url}</span>}
                  />
                  <span className="set-muted">{say('connect.eventCount', { n: events.length, of: WEBHOOK_EVENTS.length })}</span>
                  <span className="set-muted connect-clip">
                    {w.branchIds.length === 0 ? say('connect.allBranches') : branchSummary(w.branchIds, branchesHere, lang)}
                  </span>
                  <span>
                    <StatusChip tone={w.active ? 'ok' : undefined}>{say(w.active ? 'connect.on' : 'connect.off')}</StatusChip>
                  </span>
                  <RowArrow />
                </ListRow>
              )
            })}
          </ListTable>
        )}
      </ListCard>
      {toast}
    </>
  )
}

function HookDrawer({
  hook,
  organisation,
  branches,
  flash,
  onClose,
  onCreated,
  onSecret,
  onChanged,
  onDeleted,
}: {
  /** null for a new endpoint. */
  hook: Webhook | null
  organisation?: string
  branches: Branch[]
  flash: (text: string) => void
  onClose: () => void
  onCreated: (webhook: Webhook, secret: string) => void
  onSecret: (id: string, secret: string) => void
  onChanged: () => Promise<void>
  onDeleted: (id: string) => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const noop = useCallback(() => {}, [])
  const { busy, error, setError, save } = useSave(noop, flash)
  const [d, setD] = useState<WebhookDraft>(hook ? draftOf(hook) : emptyDraft())
  const [tried, setTried] = useState(false)
  const [askSecret, setAskSecret] = useState(false)
  const [test, setTest] = useState<WebhookTestResult | null>(null)
  const [testing, setTesting] = useState(false)
  const problems = webhookProblems(d, lang)
  const changed = hook !== null && JSON.stringify(d) !== JSON.stringify(draftOf(hook))
  const set = (patch: Partial<WebhookDraft>) => setD((x) => ({ ...x, ...patch }))
  const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item])

  async function submit() {
    // Save names every problem, and sends nothing while there is one.
    if (problems.length > 0) {
      setTried(true)
      return
    }
    if (!hook) {
      const made: { answer?: { webhook: Webhook; secret: string } } = {}
      const ok = await save(async () => {
        made.answer = await api.createWebhook(organisation, d)
      })
      if (ok && made.answer) onCreated(made.answer.webhook, made.answer.secret)
      return
    }
    const ok = await save(() => api.updateWebhook(hook.id, d), say('connect.saved'))
    if (ok) await onChanged()
  }

  async function runTest() {
    if (!hook) return
    setTesting(true)
    setTest(null)
    setError('')
    try {
      setTest(await api.testWebhook(hook.id))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setTesting(false)
    }
  }

  async function newSecret() {
    if (!hook) return
    let secret = ''
    const ok = await save(async () => {
      secret = (await api.newWebhookSecret(hook.id)).secret
    })
    if (ok) onSecret(hook.id, secret)
  }

  return (
    <InnerPage
      back={say('admin.section.webhooks')}
      title={hook ? hook.name : say('connect.newEndpoint')}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel={hook ? say('common.save') : say('webhooks.add')}
      busy={busy}
      extra={
        hook && (
          <Confirm
            ask={say('common.delete')}
            question={say('connect.deleteHookAsk')}
            yes={say('common.delete')}
            keep={say('connect.keep')}
            busy={busy}
            onYes={() =>
              void save(() => api.deleteWebhook(hook.id), say('connect.deleted')).then((ok) => { if (ok) void onDeleted(hook.id) })
            }
          />
        )
      }
    >
      {error && <Notice>{error}</Notice>}
      {tried && problems.length > 0 && <Notice>{problems.join(' ')}</Notice>}

      <label className="field">
        <span>{say('webhooks.name')}</span>
        <Input fullWidth value={d.name} onChange={(e) => set({ name: e.target.value })} />
      </label>

      <label className="field">
        <span>{say('webhooks.url')}</span>
        <Input
          fullWidth
          className="connect-mono-input"
          value={d.url}
          onChange={(e) => set({ url: e.target.value })}
          placeholder={say('connect.egHookUrl')}
        />
      </label>
      {isPlainHttp(d.url) && (
        <p className="connect-warn">
          <Icon name="alert" size={15} /> {say('connect.notHttps')}
        </p>
      )}

      <fieldset className="field">
        <legend>{say('connect.sendWhen')}</legend>
        <div className="connect-events">
          {WEBHOOK_EVENTS.map((e) => (
            <Tick key={e} checked={d.events.includes(e)} onChange={() => set({ events: toggle(d.events, e) })}>
              <span className="connect-event">
                {say(EVENT_LABEL[e])}
                <code className="connect-evcode">{e}</code>
              </span>
            </Tick>
          ))}
        </div>
      </fieldset>

      <fieldset className="field">
        <legend>{say('connect.fromBranches')}</legend>
        <div className="connect-branches">
          {branches.map((b) => (
            <Tick key={b.id} checked={d.branchIds.includes(b.id)} onChange={() => set({ branchIds: toggle(d.branchIds, b.id) })}>
              {b.name}
            </Tick>
          ))}
        </div>
        {d.branchIds.length === 0 && <small>{say('connect.everyBranch')}</small>}
      </fieldset>

      <Toggle title={say('connect.on')} hint={say('connect.offDrops')} checked={d.active} onChange={(active) => set({ active })} />

      {hook && (
        <fieldset className="field connect-group">
          <legend>{say('connect.testAndSecret')}</legend>
          <div className="connect-line">
            <span>
              <strong>{say('connect.test')}</strong>
              {changed && <span className="muted">{say('connect.usesSaved')}</span>}
            </span>
            <Button variant="secondary" isDisabled={testing || busy} onPress={() => void runTest()}>
              <Mark name="send" size={16} /> {say('webhooks.test')}
            </Button>
          </div>
          {test && (
            <TestResult
              ok={test.delivered}
              title={say(test.delivered ? 'deliveries.status.delivered' : 'deliveries.status.failed')}
            >
              <span>
                {test.delivered
                  ? say('webhooks.testOk', { name: hook.name, status: test.status, ms: test.tookMs })
                  : say('webhooks.testFailed', { name: hook.name, error: test.error ?? String(test.status) })}
              </span>
            </TestResult>
          )}
          <div className="connect-line">
            <span>
              <strong>{say('connect.secret')}</strong>
              <Masked>
                <span className="muted">{say('connect.secretHidden')}</span>
              </Masked>
            </span>
            {!askSecret && (
              <Button variant="secondary" isDisabled={busy} onPress={() => setAskSecret(true)}>
                <Icon name="refresh" size={16} /> {say('webhooks.newSecret')}
              </Button>
            )}
          </div>
          {askSecret && (
            <div className="connect-ask">
              <p>{say('connect.newSecretAsk')}</p>
              <div>
                <Button variant="danger" isDisabled={busy} onPress={() => void newSecret()}>
                  {say('connect.replace')}
                </Button>
                <Button variant="ghost" onPress={() => setAskSecret(false)}>
                  {say('connect.keep')}
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      )}
    </InnerPage>
  )
}
